import React, { useState, useEffect, useContext, useRef } from 'react';
import inventoryService from '../../services/inventoryService';
import api from '../../services/api';
import Modal from '../../comun/Modal';
import { AuthContext } from '../../context/AuthContext';

const Bodega = () => {
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_FAST_FOOD' || roleName === 'ADMIN_CLINIC' || user?.is_superuser;

    // Tabs: armazones, lunas, accesorios, insumos, movimientos, unidades
    const [activeTab, setActiveTab] = useState('armazones');
    const [viewMode, setViewMode] = useState('table'); // 'table' | 'grid'
    const [searchTerm, setSearchTerm] = useState('');
    const [filterGender, setFilterGender] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'DISPONIBLE' | 'VENDIDO'
    const [filterSucursal, setFilterSucursal] = useState('all'); // 'all' | 'Sur' | 'Norte'

    // Estados generales
    const [supplies, setSupplies] = useState([]);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modales
    const [isSupplyModalOpen, setIsSupplyModalOpen] = useState(false);
    const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importSucursal, setImportSucursal] = useState(user?.sucursal || 'Sur');
    const [importVendidos, setImportVendidos] = useState(true);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [fileInputRef, setFileInputRef] = useState(null);
    const [selectedSupplies, setSelectedSupplies] = useState([]);
    const [isBulkMoving, setIsBulkMoving] = useState(false);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);
    const [clearScope, setClearScope] = useState('all');
    const [clearLoading, setClearLoading] = useState(false);

    // Formulario de Producto / Armazón
    const [editingSupply, setEditingSupply] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    const initialFormState = {
        name: '',
        description: '',
        supply_type: 'armazon',
        brand: '',
        model_code: '',
        linea: 'CLASICA',
        material: 'Acetato',
        color: '',
        size_caliber: '',
        gender: 'Hombre',
        sucursal: user?.sucursal || 'Sur',
        status_armazon: 'DISPONIBLE',
        cost_per_unit: 0,
        sale_price: 20,
        wholesale_price: 23,
        iva: 15,
        current_stock: 1,
        minimum_stock: 1,
        unit: '',
        presentation: '',
        expiration_date: '',
        is_active: true
    };

    const [supplyForm, setSupplyForm] = useState(initialFormState);
    const [unitOptions, setUnitOptions] = useState([]);
    const [units, setUnits] = useState([]);
    const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
    const [editingUnit, setEditingUnit] = useState(null);
    const [unitForm, setUnitForm] = useState({ name: '', abbreviation: '' });

    // Movimiento
    const [movementForm, setMovementForm] = useState({
        supply: '',
        supplyName: '',
        movement_type: 'in',
        quantity: 1,
        reason: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [suppliesData, unitsData] = await Promise.all([
                inventoryService.getSupplies(),
                inventoryService.getUnits()
            ]);
            setSupplies(suppliesData || []);
            setUnits(unitsData || []);

            const mappedUnits = (unitsData || []).map(u => ({
                value: u.id,
                display_name: `${u.name} (${u.abbreviation})`
            }));
            setUnitOptions(mappedUnits);

            if (activeTab === 'movimientos') {
                const movs = await inventoryService.getMovements();
                setMovements(movs || []);
            }
        } catch (error) {
            console.error("Error al cargar inventario:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const getImageUrl = (img) => {
        if (!img) return null;
        if (img.startsWith('http://') || img.startsWith('https://')) return img;
        const apiBase = process.env.REACT_APP_API_URL || '';
        return `${apiBase}${img.startsWith('/') ? '' : '/'}${img}`;
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const openNewModal = (defaultType = 'armazon') => {
        setEditingSupply(null);
        setImageFile(null);
        setImagePreview('');
        setSupplyForm({
            ...initialFormState,
            supply_type: defaultType,
            sucursal: user?.sucursal || (filterSucursal !== 'all' ? filterSucursal : 'Sur'),
            unit: unitOptions.length > 0 ? unitOptions[0].value : ''
        });
        setIsSupplyModalOpen(true);
    };

    const openEditModal = (s) => {
        setEditingSupply(s);
        setImageFile(null);
        setImagePreview(getImageUrl(s.image) || '');
        setSupplyForm({
            name: s.name || '',
            description: s.description || '',
            supply_type: s.supply_type || 'armazon',
            brand: s.brand || '',
            model_code: s.model_code || '',
            linea: s.linea || 'CLASICA',
            material: s.material || '',
            color: s.color || '',
            size_caliber: s.size_caliber || '',
            gender: s.gender || 'Hombre',
            sucursal: s.sucursal || (user?.sucursal || 'Sur'),
            status_armazon: s.status_armazon || (Number(s.current_stock) > 0 ? 'DISPONIBLE' : 'VENDIDO'),
            cost_per_unit: s.cost_per_unit || 0,
            sale_price: s.sale_price || 0,
            wholesale_price: s.wholesale_price || 0,
            iva: s.iva ?? 15,
            current_stock: Math.round(Number(s.current_stock) || 0),
            minimum_stock: Math.round(Number(s.minimum_stock) || 1),
            unit: s.unit || (unitOptions.length > 0 ? unitOptions[0].value : ''),
            presentation: s.presentation || '',
            expiration_date: s.expiration_date || '',
            is_active: s.is_active !== undefined ? s.is_active : true
        });
        setIsSupplyModalOpen(true);
    };

    const handleSupplySubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('name', supplyForm.name);
            formData.append('description', supplyForm.description || '');
            formData.append('supply_type', supplyForm.supply_type);
            formData.append('brand', supplyForm.brand || '');
            formData.append('model_code', supplyForm.model_code || '');
            formData.append('linea', supplyForm.linea || '');
            formData.append('material', supplyForm.material || '');
            formData.append('color', supplyForm.color || '');
            formData.append('size_caliber', supplyForm.size_caliber || '');
            formData.append('gender', supplyForm.gender || 'Hombre');
            formData.append('sucursal', supplyForm.sucursal || '');
            formData.append('status_armazon', supplyForm.status_armazon || 'DISPONIBLE');
            formData.append('cost_per_unit', supplyForm.cost_per_unit || 0);
            formData.append('sale_price', supplyForm.sale_price || 0);
            formData.append('wholesale_price', supplyForm.wholesale_price || 0);
            formData.append('iva', supplyForm.iva ?? 15);
            formData.append('minimum_stock', supplyForm.minimum_stock || 1);
            if (supplyForm.unit) formData.append('unit', supplyForm.unit);
            if (supplyForm.presentation) formData.append('presentation', supplyForm.presentation);
            if (supplyForm.expiration_date) formData.append('expiration_date', supplyForm.expiration_date);
            formData.append('is_active', supplyForm.is_active);

            if (imageFile) {
                formData.append('image', imageFile);
            }

            if (editingSupply) {
                await inventoryService.updateSupply(editingSupply.id, formData);
            } else {
                const stockToSave = supplyForm.status_armazon === 'VENDIDO' ? 0 : (supplyForm.current_stock || 0);
                formData.append('current_stock', stockToSave);
                const created = await inventoryService.createSupply(formData);
                const initStock = parseFloat(stockToSave) || 0;
                if (initStock > 0 && created?.id) {
                    await inventoryService.recordMovement(created.id, {
                        movement_type: 'in',
                        quantity: initStock,
                        reason: 'Inventario inicial'
                    });
                }
            }

            setIsSupplyModalOpen(false);
            fetchData();
        } catch (error) {
            console.error("Error al guardar producto:", error);
            alert('Error al guardar el producto. Verifique los campos obligatorios.');
        }
    };

    const handleImportSubmit = async (e) => {
        e.preventDefault();
        if (!importFile) {
            alert('Por favor selecciona un archivo Excel (.xlsx o .xls)');
            return;
        }
        setImportLoading(true);
        setImportResult(null);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            formData.append('sucursal', importSucursal);
            formData.append('import_vendidos', importVendidos ? 'true' : 'false');

            const res = await inventoryService.importSuppliesExcel(formData, {
                sucursal: importSucursal,
                import_vendidos: importVendidos ? 'true' : 'false'
            });
            setImportResult(res);
            fetchData();
        } catch (error) {
            console.error('Error al importar:', error);
            const serverError = error.response?.data?.error || error.response?.data?.detail;
            const msg = serverError || 
                        (error.code === 'ECONNABORTED' ? 'Tiempo de espera agotado (timeout). El archivo es muy grande o el servidor tardó en responder.' : '') ||
                        (error.message === 'Network Error' ? 'Error de red o conexión al servidor.' : '') ||
                        error.message || 
                        'Error al procesar el archivo Excel. Verifica el formato.';
            alert(msg);
        } finally {
            setImportLoading(false);
        }
    };

    const handleBulkMoveSucursal = async (targetSucursal) => {
        if (!selectedSupplies.length) return;
        if (!window.confirm(`¿Reasignar los ${selectedSupplies.length} armazones seleccionados a la Sucursal ${targetSucursal}?`)) {
            return;
        }
        setIsBulkMoving(true);
        try {
            await inventoryService.bulkChangeSucursal(selectedSupplies, targetSucursal);
            setSelectedSupplies([]);
            fetchData();
        } catch (error) {
            console.error('Error al mover armazones:', error);
            alert('Error al reasignar sucursal a los armazones seleccionados.');
        } finally {
            setIsBulkMoving(false);
        }
    };

    const handleSingleMoveSucursal = async (supplyId, targetSucursal) => {
        try {
            await inventoryService.bulkChangeSucursal([supplyId], targetSucursal);
            fetchData();
        } catch (error) {
            console.error('Error al cambiar sucursal:', error);
            alert('Error al cambiar sucursal del armazón.');
        }
    };

    const handleClearInventory = async (e) => {
        e.preventDefault();
        const scopeLabel = clearScope === 'Sur' ? 'de Sucursal Sur' : clearScope === 'Norte' ? 'de Sucursal Norte' : 'de TODAS las sucursales';
        if (!window.confirm(`¿CONFIRMAS ELIMINAR TODO EL INVENTARIO DE ARMAZONES ${scopeLabel.toUpperCase()}?\n\nEsta acción no se puede deshacer y te permitirá volver a importar el Excel limpiamente.`)) {
            return;
        }

        setClearLoading(true);
        try {
            const res = await inventoryService.clearInventory({
                sucursal: clearScope,
                supply_type: 'armazon'
            });
            alert(res.message || 'Inventario eliminado con éxito. Ya puedes volver a importar tu archivo Excel.');
            setIsClearModalOpen(false);
            setSelectedSupplies([]);
            fetchData();
        } catch (error) {
            console.error('Error al vaciar inventario:', error);
            const msg = error.response?.data?.error || 'Error al eliminar el inventario.';
            alert(msg);
        } finally {
            setClearLoading(false);
        }
    };

    const handleDeleteSupply = async (id) => {
        if (window.confirm('¿Está seguro de eliminar este producto del inventario?')) {
            try {
                await inventoryService.deleteSupply(id);
                fetchData();
            } catch (error) {
                alert('Error al eliminar el producto');
            }
        }
    };

    const handleMovementSubmit = async (e) => {
        e.preventDefault();
        try {
            await inventoryService.recordMovement(movementForm.supply, {
                movement_type: movementForm.movement_type,
                quantity: parseFloat(movementForm.quantity),
                reason: movementForm.reason || 'Ajuste de inventario'
            });
            setIsMovementModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Error al registrar movimiento.');
        }
    };

    const handleUnitSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingUnit) {
                await inventoryService.updateUnit(editingUnit.id, unitForm);
            } else {
                await inventoryService.createUnit(unitForm);
            }
            setIsUnitModalOpen(false);
            fetchData();
        } catch (error) {
            alert('Error al guardar unidad.');
        }
    };

    const handleDeleteUnit = async (id) => {
        if (window.confirm('¿Eliminar esta unidad de medida?')) {
            try {
                await inventoryService.deleteUnit(id);
                fetchData();
            } catch (error) {
                alert('No se pudo eliminar la unidad. Puede que esté asociada a productos.');
            }
        }
    };

    // Filtrar productos según la pestaña activa
    const getFilteredSupplies = () => {
        let list = supplies;

        if (activeTab === 'armazones') {
            list = list.filter(s => s.supply_type === 'armazon');
        } else if (activeTab === 'lunas') {
            list = list.filter(s => s.supply_type === 'luna' || s.supply_type === 'lente_contacto');
        } else if (activeTab === 'accesorios') {
            list = list.filter(s => s.supply_type === 'accesorio' || s.supply_type === 'gotas');
        } else if (activeTab === 'insumos') {
            list = list.filter(s => ['insumo', 'instrumental', 'en_uso', 'bodega'].includes(s.supply_type));
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(s =>
                (s.name && s.name.toLowerCase().includes(term)) ||
                (s.brand && s.brand.toLowerCase().includes(term)) ||
                (s.model_code && s.model_code.toLowerCase().includes(term)) ||
                (s.linea && s.linea.toLowerCase().includes(term)) ||
                (s.sucursal && s.sucursal.toLowerCase().includes(term)) ||
                (s.material && s.material.toLowerCase().includes(term)) ||
                (s.color && s.color.toLowerCase().includes(term))
            );
        }

        if (filterGender !== 'all' && activeTab === 'armazones') {
            list = list.filter(s => (s.gender || '').toLowerCase() === filterGender.toLowerCase());
        }

        if (filterStatus !== 'all' && activeTab === 'armazones') {
            if (filterStatus === 'DISPONIBLE') {
                list = list.filter(s => s.status_armazon === 'DISPONIBLE' && Number(s.current_stock) > 0);
            } else if (filterStatus === 'VENDIDO') {
                list = list.filter(s => s.status_armazon === 'VENDIDO' || Number(s.current_stock) <= 0);
            }
        }

        if (filterSucursal !== 'all' && activeTab === 'armazones') {
            list = list.filter(s => (s.sucursal || '').toLowerCase().includes(filterSucursal.toLowerCase()));
        }

        return list;
    };

    const filteredList = getFilteredSupplies();

    // Paginación (15 por página)
    const ITEMS_PER_PAGE = 15;
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterGender, filterStatus, filterSucursal, activeTab]);

    const totalPages = Math.ceil(filteredList.length / ITEMS_PER_PAGE) || 1;
    const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
    const startIndex = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredList.length);
    const currentItems = filteredList.slice(startIndex, endIndex);

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (safeCurrentPage <= 4) {
                pages.push(1, 2, 3, 4, 5, '...', totalPages);
            } else if (safeCurrentPage >= totalPages - 3) {
                pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', safeCurrentPage - 1, safeCurrentPage, safeCurrentPage + 1, '...', totalPages);
            }
        }
        return pages;
    };

    // Scroll horizontal superior sincronizado para la tabla
    const topScrollRef = useRef(null);
    const tableContainerRef = useRef(null);
    const [tableScrollWidth, setTableScrollWidth] = useState(0);
    const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);

    useEffect(() => {
        const updateWidth = () => {
            if (tableContainerRef.current) {
                const sw = tableContainerRef.current.scrollWidth;
                const cw = tableContainerRef.current.clientWidth;
                setTableScrollWidth(sw);
                setHasHorizontalOverflow(sw > cw + 1);
            }
        };
        updateWidth();
        const t1 = setTimeout(updateWidth, 100);
        const t2 = setTimeout(updateWidth, 400);
        window.addEventListener('resize', updateWidth);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            window.removeEventListener('resize', updateWidth);
        };
    }, [filteredList, currentItems, activeTab, viewMode, safeCurrentPage]);

    const handleTopScroll = (e) => {
        if (tableContainerRef.current && Math.abs(tableContainerRef.current.scrollLeft - e.target.scrollLeft) > 1) {
            tableContainerRef.current.scrollLeft = e.target.scrollLeft;
        }
    };

    const handleTableScroll = (e) => {
        if (topScrollRef.current && Math.abs(topScrollRef.current.scrollLeft - e.target.scrollLeft) > 1) {
            topScrollRef.current.scrollLeft = e.target.scrollLeft;
        }
    };

    // Contadores para badges en tabs
    const countArmazones = supplies.filter(s => s.supply_type === 'armazon').length;
    const countDisponibles = supplies.filter(s => s.supply_type === 'armazon' && s.status_armazon !== 'VENDIDO' && Number(s.current_stock) > 0).length;
    const countVendidos = supplies.filter(s => s.supply_type === 'armazon' && (s.status_armazon === 'VENDIDO' || Number(s.current_stock) <= 0)).length;
    const countLunas = supplies.filter(s => s.supply_type === 'luna' || s.supply_type === 'lente_contacto').length;
    const countAccesorios = supplies.filter(s => s.supply_type === 'accesorio' || s.supply_type === 'gotas').length;
    const countInsumos = supplies.filter(s => ['insumo', 'instrumental', 'en_uso', 'bodega'].includes(s.supply_type)).length;

    return (
        <div style={{ padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
            {/* Encabezado */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ fontSize: '1.85rem', fontWeight: '800', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="bi bi-eyeglasses" style={{ color: '#2563eb' }}></i> Inventario Óptico y Armazones
                    </h1>
                    <p style={{ margin: '0.35rem 0 0', color: '#64748b', fontSize: '0.95rem' }}>
                        Catálogo de armazones por sucursal (Sur / Norte), disponibles y vendidos con importación masiva desde Excel.
                    </p>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {['armazones', 'lunas', 'accesorios', 'insumos'].includes(activeTab) && (
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', display: 'flex', padding: '2px' }}>
                            <button
                                onClick={() => setViewMode('table')}
                                style={{
                                    border: 'none', background: viewMode === 'table' ? '#0f172a' : 'transparent',
                                    color: viewMode === 'table' ? '#fff' : '#64748b', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'
                                }}
                                title="Vista en Listado / Tabla"
                            >
                                <i className="bi bi-list-ul"></i> Listado
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                style={{
                                    border: 'none', background: viewMode === 'grid' ? '#0f172a' : 'transparent',
                                    color: viewMode === 'grid' ? '#fff' : '#64748b', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600'
                                }}
                                title="Vista en Cuadrícula / Galería"
                            >
                                <i className="bi bi-grid-fill"></i> Galería
                            </button>
                        </div>
                    )}

                    {activeTab === 'armazones' && (
                        <button
                            onClick={() => {
                                setImportFile(null);
                                setImportResult(null);
                                setImportSucursal(filterSucursal !== 'all' ? filterSucursal : (user?.sucursal || 'Sur'));
                                setIsImportModalOpen(true);
                            }}
                            style={{
                                backgroundColor: '#059669', color: '#fff', border: 'none', padding: '0.75rem 1.3rem',
                                borderRadius: '10px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)', transition: 'all 0.2s'
                            }}
                            title="Importar armazones desde archivo Excel (.xlsx)"
                        >
                            <i className="bi bi-file-earmark-excel"></i>
                            Importar Excel (.xlsx)
                        </button>
                    )}

                    {isAdmin && activeTab === 'armazones' && (
                        <button
                            onClick={() => {
                                setClearScope(filterSucursal !== 'all' ? filterSucursal : 'all');
                                setIsClearModalOpen(true);
                            }}
                            style={{
                                backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', padding: '0.75rem 1.2rem',
                                borderRadius: '10px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                                transition: 'all 0.2s'
                            }}
                            title="Eliminar inventario de armazones para volver a cargarlo desde cero"
                        >
                            <i className="bi bi-trash3-fill"></i>
                            Eliminar Todo el Inventario
                        </button>
                    )}

                    <button
                        onClick={() => openNewModal(activeTab === 'armazones' ? 'armazon' : activeTab === 'lunas' ? 'luna' : activeTab === 'accesorios' ? 'accesorio' : 'insumo')}
                        style={{
                            backgroundColor: '#2563eb', color: '#fff', border: 'none', padding: '0.75rem 1.4rem',
                            borderRadius: '10px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)', transition: 'all 0.2s'
                        }}
                    >
                        <i className="bi bi-plus-lg"></i>
                        {activeTab === 'armazones' ? '+ Nuevo Armazón' : activeTab === 'lunas' ? '+ Nueva Luna / LC' : '+ Nuevo Producto'}
                    </button>
                </div>
            </div>

            {/* Pestañas de Categorías */}
            <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto' }}>
                <button
                    onClick={() => setActiveTab('armazones')}
                    style={{
                        padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700',
                        backgroundColor: activeTab === 'armazones' ? '#0f172a' : 'transparent',
                        color: activeTab === 'armazones' ? '#fff' : '#475569', display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    <i className="bi bi-eyeglasses"></i> Armazones y Monturas
                    <span style={{ backgroundColor: activeTab === 'armazones' ? '#334155' : '#e2e8f0', color: activeTab === 'armazones' ? '#fff' : '#475569', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>
                        {countArmazones}
                    </span>
                </button>

                <button
                    onClick={() => setActiveTab('lunas')}
                    style={{
                        padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700',
                        backgroundColor: activeTab === 'lunas' ? '#0f172a' : 'transparent',
                        color: activeTab === 'lunas' ? '#fff' : '#475569', display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    <i className="bi bi-circle"></i> Lunas y Lentes de Contacto
                    <span style={{ backgroundColor: activeTab === 'lunas' ? '#334155' : '#e2e8f0', color: activeTab === 'lunas' ? '#fff' : '#475569', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>
                        {countLunas}
                    </span>
                </button>

                <button
                    onClick={() => setActiveTab('accesorios')}
                    style={{
                        padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700',
                        backgroundColor: activeTab === 'accesorios' ? '#0f172a' : 'transparent',
                        color: activeTab === 'accesorios' ? '#fff' : '#475569', display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    <i className="bi bi-droplet-half"></i> Accesorios, Estuches y Gotas
                    <span style={{ backgroundColor: activeTab === 'accesorios' ? '#334155' : '#e2e8f0', color: activeTab === 'accesorios' ? '#fff' : '#475569', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>
                        {countAccesorios}
                    </span>
                </button>

                <button
                    onClick={() => setActiveTab('insumos')}
                    style={{
                        padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700',
                        backgroundColor: activeTab === 'insumos' ? '#0f172a' : 'transparent',
                        color: activeTab === 'insumos' ? '#fff' : '#475569', display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    <i className="bi bi-box-seam"></i> Insumos Clínicos
                    <span style={{ backgroundColor: activeTab === 'insumos' ? '#334155' : '#e2e8f0', color: activeTab === 'insumos' ? '#fff' : '#475569', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>
                        {countInsumos}
                    </span>
                </button>

                {isAdmin && (
                    <button
                        onClick={() => setActiveTab('movimientos')}
                        style={{
                            padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700',
                            backgroundColor: activeTab === 'movimientos' ? '#0f172a' : 'transparent',
                            color: activeTab === 'movimientos' ? '#fff' : '#475569', display: 'flex', alignItems: 'center', gap: '8px'
                        }}
                    >
                        <i className="bi bi-arrow-left-right"></i> Movimientos
                    </button>
                )}

                {isAdmin && (
                    <button
                        onClick={() => setActiveTab('unidades')}
                        style={{
                            padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700',
                            backgroundColor: activeTab === 'unidades' ? '#0f172a' : 'transparent',
                            color: activeTab === 'unidades' ? '#fff' : '#475569', display: 'flex', alignItems: 'center', gap: '8px'
                        }}
                    >
                        <i className="bi bi-rulers"></i> Unidades
                    </button>
                )}
            </div>

            {/* Barra de Búsqueda y Filtros */}
            {['armazones', 'lunas', 'accesorios', 'insumos'].includes(activeTab) && (
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: '260px' }}>
                        <i className="bi bi-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
                        <input
                            type="text"
                            placeholder={activeTab === 'armazones' ? "Buscar por marca, modelo, código, material o color..." : "Buscar producto..."}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%', padding: '0.75rem 1rem 0.75rem 2.5rem', borderRadius: '10px',
                                border: '1px solid #cbd5e1', backgroundColor: '#fff', fontSize: '0.95rem', boxSizing: 'border-box'
                            }}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8' }}
                            >
                                <i className="bi bi-x-circle-fill"></i>
                            </button>
                        )}
                    </div>

                    {activeTab === 'armazones' && (
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* Filtro Sucursal */}
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#475569' }}>
                                    <i className="bi bi-shop me-1"></i>Sucursal:
                                </span>
                                {['all', 'Sur', 'Norte'].map(suc => (
                                    <button
                                        key={suc}
                                        onClick={() => setFilterSucursal(suc)}
                                        style={{
                                            border: '1px solid', borderColor: filterSucursal === suc ? '#4f46e5' : '#cbd5e1',
                                            backgroundColor: filterSucursal === suc ? '#eef2ff' : '#fff',
                                            color: filterSucursal === suc ? '#4f46e5' : '#475569',
                                            padding: '5px 10px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer'
                                        }}
                                    >
                                        {suc === 'all' ? 'Todas' : `Sucursal ${suc}`}
                                    </button>
                                ))}
                            </div>

                            {/* Filtro Estado */}
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#475569' }}>Estado:</span>
                                <button
                                    onClick={() => setFilterStatus('all')}
                                    style={{
                                        border: '1px solid', borderColor: filterStatus === 'all' ? '#0f172a' : '#cbd5e1',
                                        backgroundColor: filterStatus === 'all' ? '#0f172a' : '#fff',
                                        color: filterStatus === 'all' ? '#fff' : '#475569',
                                        padding: '5px 10px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer'
                                    }}
                                >
                                    Todos ({countArmazones})
                                </button>
                                <button
                                    onClick={() => setFilterStatus('DISPONIBLE')}
                                    style={{
                                        border: '1px solid', borderColor: filterStatus === 'DISPONIBLE' ? '#059669' : '#cbd5e1',
                                        backgroundColor: filterStatus === 'DISPONIBLE' ? '#ecfdf5' : '#fff',
                                        color: filterStatus === 'DISPONIBLE' ? '#059669' : '#475569',
                                        padding: '5px 10px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer'
                                    }}
                                >
                                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', marginRight: '6px' }}></span>
                                    Disponibles ({countDisponibles})
                                </button>
                                <button
                                    onClick={() => setFilterStatus('VENDIDO')}
                                    style={{
                                        border: '1px solid', borderColor: filterStatus === 'VENDIDO' ? '#dc2626' : '#cbd5e1',
                                        backgroundColor: filterStatus === 'VENDIDO' ? '#fef2f2' : '#fff',
                                        color: filterStatus === 'VENDIDO' ? '#dc2626' : '#475569',
                                        padding: '5px 10px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer'
                                    }}
                                >
                                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444', marginRight: '6px' }}></span>
                                    Vendidos ({countVendidos})
                                </button>
                            </div>

                            {/* Filtro Categoría / Género */}
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#475569' }}>Categoría:</span>
                                {['all', 'Hombre', 'Niña', 'Niño', 'Mujer', 'Unisex'].map(g => (
                                    <button
                                        key={g}
                                        onClick={() => setFilterGender(g)}
                                        style={{
                                            border: '1px solid', borderColor: filterGender === g ? '#2563eb' : '#cbd5e1',
                                            backgroundColor: filterGender === g ? '#eff6ff' : '#fff',
                                            color: filterGender === g ? '#2563eb' : '#475569',
                                            padding: '5px 10px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer'
                                        }}
                                    >
                                        {g === 'all' ? 'Todas' : g}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* CONTENIDO PRINCIPAL: GALERÍA DE ARMAZONES / PRODUCTOS */}
            {['armazones', 'lunas', 'accesorios', 'insumos'].includes(activeTab) && (
                <div>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                            <i className="bi bi-arrow-repeat spin" style={{ fontSize: '2rem' }}></i>
                            <p style={{ marginTop: '0.5rem' }}>Cargando catálogo...</p>
                        </div>
                    ) : filteredList.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '4rem 2rem', backgroundColor: '#fff', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
                            <i className="bi bi-eyeglasses" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
                            <h3 style={{ margin: '1rem 0 0.5rem', color: '#334155' }}>No se encontraron productos</h3>
                            <p style={{ color: '#64748b', fontSize: '0.95rem' }}>
                                {searchTerm ? 'Intenta con otro término de búsqueda.' : 'Agrega el primer producto o armazón haciendo clic en el botón superior.'}
                            </p>
                        </div>
                    ) : viewMode === 'grid' ? (
                        /* VISTA EN CUADRÍCULA / TARJETAS VISUALES CON IMÁGENES */
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
                            {currentItems.map(item => {
                                const stock = Number(item.current_stock) || 0;
                                const minStock = Number(item.minimum_stock) || 1;
                                const isLowStock = stock <= minStock;
                                const imgUrl = getImageUrl(item.image);

                                return (
                                    <div
                                        key={item.id}
                                        style={{
                                            backgroundColor: '#fff', borderRadius: '16px', overflow: 'hidden',
                                            border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                                            display: 'flex', flexDirection: 'column', transition: 'transform 0.2s, box-shadow 0.2s'
                                        }}
                                    >
                                        {/* Foto del Armazón / Producto */}
                                        <div style={{ height: '180px', backgroundColor: '#f1f5f9', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                            {imgUrl ? (
                                                <img
                                                    src={imgUrl}
                                                    alt={item.name}
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }}
                                                />
                                            ) : (
                                                <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                                                    <i className="bi bi-camera" style={{ fontSize: '2.5rem' }}></i>
                                                    <p style={{ margin: '4px 0 0', fontSize: '0.8rem' }}>Sin fotografía</p>
                                                </div>
                                            )}

                                            {/* Badge de Marca */}
                                            {item.brand && (
                                                <span style={{
                                                    position: 'absolute', top: '10px', left: '10px',
                                                    backgroundColor: '#0f172a', color: '#fff', fontSize: '11px',
                                                    fontWeight: '700', padding: '4px 10px', borderRadius: '20px', letterSpacing: '0.5px'
                                                }}>
                                                    {item.brand}
                                                </span>
                                            )}

                                            {/* Badge de Stock */}
                                            <span style={{
                                                position: 'absolute', top: '10px', right: '10px',
                                                backgroundColor: stock === 0 ? '#ef4444' : isLowStock ? '#f59e0b' : '#10b981',
                                                color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 8px', borderRadius: '12px'
                                            }}>
                                                {stock === 0 ? 'Sin Stock' : `Stock: ${Math.round(stock)}`}
                                            </span>
                                        </div>

                                        {/* Detalles del Producto */}
                                        <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
                                            <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>
                                                {item.name}
                                            </h3>

                                            {item.model_code && (
                                                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem' }}>
                                                    <strong>Código/Modelo:</strong> {item.model_code}
                                                </div>
                                            )}

                                            {/* Atributos Ópticos */}
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '0.75rem' }}>
                                                {item.material && (
                                                    <span style={{ fontSize: '11px', backgroundColor: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px' }}>
                                                        <i className="bi bi-shield me-1"></i> {item.material}
                                                    </span>
                                                )}
                                                {item.color && (
                                                    <span style={{ fontSize: '11px', backgroundColor: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px' }}>
                                                        <i className="bi bi-palette me-1"></i> {item.color}
                                                    </span>
                                                )}
                                                {item.size_caliber && (
                                                    <span style={{ fontSize: '11px', backgroundColor: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px' }}>
                                                        <i className="bi bi-rulers me-1"></i> {item.size_caliber}
                                                    </span>
                                                )}
                                                {item.gender && (
                                                    <span style={{ fontSize: '11px', backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '6px' }}>
                                                        {item.gender}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Precios */}
                                            <div style={{ marginTop: 'auto', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block' }}>PVP Venta</span>
                                                    <span style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a' }}>
                                                        ${Number(item.sale_price || item.cost_per_unit || 0).toFixed(2)}
                                                    </span>
                                                </div>

                                                {isAdmin && item.cost_per_unit > 0 && (
                                                    <div style={{ textAlign: 'right' }}>
                                                        <span style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block' }}>Costo</span>
                                                        <span style={{ fontSize: '0.9rem', color: '#64748b' }}>
                                                            ${Number(item.cost_per_unit).toFixed(2)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Botones de Acción */}
                                            <div style={{ display: 'flex', gap: '6px', marginTop: '1rem' }}>
                                                <button
                                                    onClick={() => openEditModal(item)}
                                                    style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}
                                                >
                                                    <i className="bi bi-pencil me-1"></i> Editar
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setMovementForm({ supply: item.id, supplyName: item.name, movement_type: 'in', quantity: 1, reason: 'Ingreso de stock' });
                                                        setIsMovementModalOpen(true);
                                                    }}
                                                    style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer' }}
                                                    title="Agregar Stock (+)"
                                                >
                                                    <i className="bi bi-plus-lg"></i>
                                                </button>

                                                {isAdmin && (
                                                    <button
                                                        onClick={() => handleDeleteSupply(item.id)}
                                                        style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #fee2e2', background: '#fef2f2', color: '#ef4444', cursor: 'pointer' }}
                                                        title="Eliminar"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* VISTA EN TABLA */
                        <>
                            {/* Barra de scroll horizontal superior sincronizada */}
                            {hasHorizontalOverflow && (
                                <div
                                    ref={topScrollRef}
                                    onScroll={handleTopScroll}
                                    style={{
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        height: '14px',
                                        marginBottom: '6px',
                                        backgroundColor: '#f1f5f9',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0'
                                    }}
                                    title="Desplazar tabla horizontalmente"
                                >
                                    <div style={{ width: `${tableScrollWidth}px`, height: '1px' }} />
                                </div>
                            )}

                            <div
                                ref={tableContainerRef}
                                onScroll={handleTableScroll}
                                style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
                            >
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: activeTab === 'armazones' ? '820px' : '750px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left', fontSize: '0.82rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {activeTab === 'armazones' ? (
                                                <>
                                                    <th style={{ padding: '12px 14px', width: '40px', textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={currentItems.length > 0 && currentItems.every(s => selectedSupplies.includes(s.id))}
                                                            onChange={e => {
                                                                if (e.target.checked) {
                                                                    setSelectedSupplies(prev => Array.from(new Set([...prev, ...currentItems.map(s => s.id)])));
                                                                } else {
                                                                    setSelectedSupplies(prev => prev.filter(id => !currentItems.some(s => s.id === id)));
                                                                }
                                                            }}
                                                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                            title="Seleccionar los 15 visibles en esta página"
                                                        />
                                                    </th>
                                                    <th style={{ padding: '12px 14px' }}>Código</th>
                                                    <th style={{ padding: '12px 14px' }}>Descripción</th>
                                                    <th style={{ padding: '12px 14px' }}>Marca</th>
                                                    <th style={{ padding: '12px 14px' }}>Categoría</th>
                                                    <th style={{ padding: '12px 14px' }}>Sucursal</th>
                                                    <th style={{ padding: '12px 14px', textAlign: 'center' }}>Stock</th>
                                                    <th style={{ padding: '12px 14px' }}>PVP Venta</th>
                                                    <th style={{ padding: '12px 14px' }}>Estado</th>
                                                    <th style={{ padding: '12px 14px', textAlign: 'center' }}>Acciones</th>
                                                </>
                                        ) : (
                                            <>
                                                <th style={{ padding: '12px 16px' }}>Foto</th>
                                                <th style={{ padding: '12px 16px' }}>Producto / Marca</th>
                                                <th style={{ padding: '12px 16px' }}>Modelo/Código</th>
                                                <th style={{ padding: '12px 16px' }}>Material / Color</th>
                                                <th style={{ padding: '12px 16px' }}>Stock</th>
                                                <th style={{ padding: '12px 16px' }}>PVP Venta</th>
                                                <th style={{ padding: '12px 16px', textAlign: 'center' }}>Acciones</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.map(item => {
                                        const stock = Number(item.current_stock) || 0;
                                        const isVendido = item.status_armazon === 'VENDIDO' || stock <= 0;
                                        const imgUrl = getImageUrl(item.image);

                                        if (activeTab === 'armazones') {
                                            return (
                                                <tr
                                                    key={item.id}
                                                    style={{
                                                        borderBottom: '1px solid #f1f5f9',
                                                        backgroundColor: selectedSupplies.includes(item.id) ? '#eff6ff' : isVendido ? '#fff5f5' : '#fff',
                                                        transition: 'background-color 0.15s'
                                                    }}
                                                >
                                                    {/* Checkbox de selección */}
                                                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSupplies.includes(item.id)}
                                                            onChange={e => {
                                                                if (e.target.checked) {
                                                                    setSelectedSupplies(prev => [...prev, item.id]);
                                                                } else {
                                                                    setSelectedSupplies(prev => prev.filter(id => id !== item.id));
                                                                }
                                                            }}
                                                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                        />
                                                    </td>

                                                    {/* Código */}
                                                    <td style={{ padding: '12px 14px' }}>
                                                        <span style={{
                                                            fontFamily: 'monospace', fontWeight: '800', fontSize: '0.88rem',
                                                            padding: '4px 8px', borderRadius: '6px',
                                                            backgroundColor: isVendido ? '#fee2e2' : '#f1f5f9',
                                                            color: isVendido ? '#991b1b' : '#0f172a',
                                                            border: '1px solid #e2e8f0', display: 'inline-block'
                                                        }}>
                                                            {item.model_code || '—'}
                                                        </span>
                                                    </td>

                                                    {/* Descripción */}
                                                    <td style={{ padding: '12px 14px' }}>
                                                        <strong style={{ color: '#0f172a', display: 'block', fontSize: '0.92rem' }}>
                                                            {item.name}
                                                        </strong>
                                                        {item.description && item.description !== item.name && (
                                                            <span style={{ fontSize: '11px', color: '#64748b' }}>{item.description}</span>
                                                        )}
                                                    </td>

                                                    {/* Marca */}
                                                    <td style={{ padding: '12px 14px', fontSize: '0.88rem', fontWeight: '700', color: '#2563eb' }}>
                                                        {item.brand || '—'}
                                                    </td>

                                                    {/* Categoría / Género */}
                                                    <td style={{ padding: '12px 14px', fontSize: '0.85rem' }}>
                                                        <span style={{
                                                            padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700',
                                                            backgroundColor: item.gender === 'Hombre' ? '#e0f2fe' : item.gender === 'Niña' ? '#fce7f3' : item.gender === 'Niño' ? '#fef3c7' : '#f1f5f9',
                                                            color: item.gender === 'Hombre' ? '#0369a1' : item.gender === 'Niña' ? '#be185d' : item.gender === 'Niño' ? '#b45309' : '#475569'
                                                        }}>
                                                            {item.gender || 'General'}
                                                        </span>
                                                    </td>

                                                    {/* Sucursal con selector rápido */}
                                                    <td style={{ padding: '12px 14px', fontSize: '0.85rem' }}>
                                                        <select
                                                            value={(item.sucursal || '').toLowerCase().includes('sur') ? 'Sur' : (item.sucursal || '').toLowerCase().includes('norte') ? 'Norte' : 'Sur'}
                                                            onChange={e => handleSingleMoveSucursal(item.id, e.target.value)}
                                                            style={{
                                                                fontSize: '11px', fontWeight: '800', padding: '4px 8px', borderRadius: '12px',
                                                                border: '1px solid',
                                                                borderColor: (item.sucursal || '').toLowerCase().includes('sur') ? '#c084fc' : '#818cf8',
                                                                backgroundColor: (item.sucursal || '').toLowerCase().includes('sur') ? '#f3e8ff' : '#e0e7ff',
                                                                color: (item.sucursal || '').toLowerCase().includes('sur') ? '#6b21a8' : '#3730a3',
                                                                cursor: 'pointer', outline: 'none'
                                                            }}
                                                            title="Haz clic para reasignar sucursal (Sur / Norte)"
                                                        >
                                                            <option value="Sur">🏢 Sucursal Sur</option>
                                                            <option value="Norte">🏢 Sucursal Norte</option>
                                                        </select>
                                                    </td>

                                                    {/* Stock */}
                                                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                                                        <span style={{
                                                            display: 'inline-block', minWidth: '32px', padding: '3px 8px', borderRadius: '8px',
                                                            fontWeight: '800', fontSize: '0.88rem',
                                                            backgroundColor: stock > 0 ? '#dcfce7' : '#fee2e2',
                                                            color: stock > 0 ? '#166534' : '#991b1b'
                                                        }}>
                                                            {Math.round(stock)}
                                                        </span>
                                                    </td>

                                                    {/* PVP Venta */}
                                                    <td style={{ padding: '12px 14px', fontWeight: '800', color: '#0f172a', fontSize: '0.92rem' }}>
                                                        ${Number(item.sale_price || 0).toFixed(2)}
                                                    </td>

                                                    {/* Estado */}
                                                    <td style={{ padding: '12px 14px' }}>
                                                        {isVendido ? (
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '800', backgroundColor: '#fee2e2', color: '#dc2626' }}>
                                                                <i className="bi bi-x-circle-fill"></i> VENDIDO
                                                            </span>
                                                        ) : (
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '800', backgroundColor: '#dcfce7', color: '#16a34a' }}>
                                                                <i className="bi bi-check-circle-fill"></i> DISPONIBLE
                                                            </span>
                                                        )}
                                                    </td>

                                                    {/* Acciones */}
                                                    <td style={{ padding: '12px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                        <button
                                                            onClick={() => openEditModal(item)}
                                                            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', color: '#334155', marginRight: '6px' }}
                                                            title="Editar armazón"
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setMovementForm({ supply: item.id, supplyName: `${item.model_code ? item.model_code + ' - ' : ''}${item.name}`, movement_type: 'in', quantity: 1, reason: 'Ingreso / Reposición' });
                                                                setIsMovementModalOpen(true);
                                                            }}
                                                            style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', marginRight: '6px' }}
                                                            title="Añadir stock (+)"
                                                        >
                                                            <i className="bi bi-plus-lg"></i>
                                                        </button>
                                                        {isAdmin && (
                                                            <button
                                                                onClick={() => handleDeleteSupply(item.id)}
                                                                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #fee2e2', background: '#fef2f2', color: '#ef4444', cursor: 'pointer' }}
                                                                title="Eliminar"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        }

                                        // Para pestañas de lunas, accesorios, insumos
                                        return (
                                            <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '10px 16px' }}>
                                                    <div style={{ width: '44px', height: '44px', borderRadius: '8px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                        {imgUrl ? (
                                                            <img src={imgUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                        ) : (
                                                            <i className="bi bi-box" style={{ color: '#94a3b8' }}></i>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '10px 16px' }}>
                                                    <strong style={{ color: '#0f172a', display: 'block' }}>{item.name}</strong>
                                                    {item.brand && <span style={{ fontSize: '11px', color: '#2563eb', fontWeight: '600' }}>{item.brand}</span>}
                                                </td>
                                                <td style={{ padding: '10px 16px', color: '#64748b', fontSize: '0.9rem' }}>{item.model_code || '-'}</td>
                                                <td style={{ padding: '10px 16px', color: '#64748b', fontSize: '0.85rem' }}>
                                                    {item.material} {item.color ? `· ${item.color}` : ''}
                                                </td>
                                                <td style={{ padding: '10px 16px' }}>
                                                    <span style={{ fontWeight: '700', color: stock <= 0 ? '#ef4444' : '#10b981' }}>
                                                        {Math.round(stock)} {item.unit_display}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '10px 16px', fontWeight: '700', color: '#0f172a' }}>
                                                    ${Number(item.sale_price || item.cost_per_unit || 0).toFixed(2)}
                                                </td>
                                                <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                                                    <button onClick={() => openEditModal(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', fontSize: '1.1rem', marginRight: '8px' }}>
                                                        <i className="bi bi-pencil-square"></i>
                                                    </button>
                                                    {isAdmin && (
                                                        <button onClick={() => handleDeleteSupply(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '1.1rem' }}>
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        </>
                    )}

                    {/* Controles de paginación (15 por página) */}
                    {filteredList.length > 0 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '1.25rem',
                            padding: '12px 18px',
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                            flexWrap: 'wrap',
                            gap: '12px'
                        }}>
                            <div style={{ fontSize: '0.86rem', color: '#64748b' }}>
                                Mostrando <strong style={{ color: '#0f172a' }}>{startIndex + 1}</strong> – <strong style={{ color: '#0f172a' }}>{endIndex}</strong> de <strong style={{ color: '#0f172a' }}>{filteredList.length}</strong> productos <span style={{ color: '#94a3b8' }}>(15 por página)</span>
                            </div>

                            {totalPages > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <button
                                        onClick={() => setCurrentPage(1)}
                                        disabled={safeCurrentPage === 1}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                            background: '#fff',
                                            cursor: safeCurrentPage === 1 ? 'not-allowed' : 'pointer',
                                            color: safeCurrentPage === 1 ? '#cbd5e1' : '#334155',
                                            fontSize: '0.82rem',
                                            fontWeight: '600'
                                        }}
                                        title="Primera página"
                                    >
                                        <i className="bi bi-chevron-double-left"></i>
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={safeCurrentPage === 1}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                            background: '#fff',
                                            cursor: safeCurrentPage === 1 ? 'not-allowed' : 'pointer',
                                            color: safeCurrentPage === 1 ? '#cbd5e1' : '#334155',
                                            fontSize: '0.82rem',
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                        title="Página anterior"
                                    >
                                        <i className="bi bi-chevron-left"></i> Ant.
                                    </button>

                                    {getPageNumbers().map((num, idx) => (
                                        num === '...' ? (
                                            <span key={`dots-${idx}`} style={{ padding: '0 6px', color: '#94a3b8' }}>...</span>
                                        ) : (
                                            <button
                                                key={num}
                                                onClick={() => setCurrentPage(num)}
                                                style={{
                                                    minWidth: '34px',
                                                    height: '34px',
                                                    padding: '0 8px',
                                                    borderRadius: '6px',
                                                    border: num === safeCurrentPage ? '1px solid #0f172a' : '1px solid #e2e8f0',
                                                    background: num === safeCurrentPage ? '#0f172a' : '#fff',
                                                    color: num === safeCurrentPage ? '#fff' : '#334155',
                                                    cursor: 'pointer',
                                                    fontSize: '0.84rem',
                                                    fontWeight: num === safeCurrentPage ? '700' : '500',
                                                    transition: 'all 0.15s'
                                                }}
                                            >
                                                {num}
                                            </button>
                                        )
                                    ))}

                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={safeCurrentPage === totalPages}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                            background: '#fff',
                                            cursor: safeCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                                            color: safeCurrentPage === totalPages ? '#cbd5e1' : '#334155',
                                            fontSize: '0.82rem',
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                        title="Página siguiente"
                                    >
                                        Sig. <i className="bi bi-chevron-right"></i>
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={safeCurrentPage === totalPages}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                            background: '#fff',
                                            cursor: safeCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                                            color: safeCurrentPage === totalPages ? '#cbd5e1' : '#334155',
                                            fontSize: '0.82rem',
                                            fontWeight: '600'
                                        }}
                                        title="Última página"
                                    >
                                        <i className="bi bi-chevron-double-right"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* TAB MOVIMIENTOS */}
            {activeTab === 'movimientos' && isAdmin && (
                <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '1.5rem', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                    <h3 style={{ margin: '0 0 1rem', fontSize: '1.2rem', fontWeight: '700', color: '#0f172a' }}>Historial de Movimientos de Inventario</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', fontSize: '0.85rem', color: '#64748b' }}>
                                <th style={{ padding: '10px 14px' }}>Fecha</th>
                                <th style={{ padding: '10px 14px' }}>Producto</th>
                                <th style={{ padding: '10px 14px' }}>Tipo</th>
                                <th style={{ padding: '10px 14px' }}>Cantidad</th>
                                <th style={{ padding: '10px 14px' }}>Motivo</th>
                                <th style={{ padding: '10px 14px' }}>Usuario</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movements.length === 0 ? (
                                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No hay movimientos registrados</td></tr>
                            ) : (
                                movements.map(m => (
                                    <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 14px', fontSize: '0.85rem' }}>{new Date(m.created_at).toLocaleString()}</td>
                                        <td style={{ padding: '10px 14px', fontWeight: '600' }}>{m.supply_name}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{
                                                fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '8px',
                                                backgroundColor: m.movement_type === 'in' ? '#dcfce7' : '#fee2e2',
                                                color: m.movement_type === 'in' ? '#15803d' : '#b91c1c'
                                            }}>
                                                {m.movement_type_display}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 14px', fontWeight: '700', color: m.movement_type === 'in' ? '#15803d' : '#b91c1c' }}>
                                            {m.movement_type === 'in' ? '+' : '-'}{Math.round(m.quantity)}
                                        </td>
                                        <td style={{ padding: '10px 14px', color: '#64748b', fontSize: '0.85rem' }}>{m.reason || '-'}</td>
                                        <td style={{ padding: '10px 14px', color: '#64748b', fontSize: '0.85rem' }}>{m.created_by || 'Admin'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* TAB UNIDADES DE MEDIDA */}
            {activeTab === 'unidades' && isAdmin && (
                <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '1.5rem', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700' }}>Unidades de Medida</h3>
                        <button
                            onClick={() => { setEditingUnit(null); setUnitForm({ name: '', abbreviation: '' }); setIsUnitModalOpen(true); }}
                            style={{ background: '#0f172a', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                        >
                            + Nueva Unidad
                        </button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', fontSize: '0.85rem', color: '#64748b' }}>
                                <th style={{ padding: '10px 14px' }}>Nombre</th>
                                <th style={{ padding: '10px 14px' }}>Abreviatura</th>
                                <th style={{ padding: '10px 14px', textAlign: 'center' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {units.map(u => (
                                <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '10px 14px', fontWeight: '600' }}>{u.name}</td>
                                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{u.abbreviation}</td>
                                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                        <button onClick={() => { setEditingUnit(u); setUnitForm({ name: u.name, abbreviation: u.abbreviation }); setIsUnitModalOpen(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', marginRight: '8px' }}>
                                            <i className="bi bi-pencil-square"></i>
                                        </button>
                                        <button onClick={() => handleDeleteUnit(u.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MODAL AGREGAR / EDITAR PRODUCTO O ARMAZÓN */}
            <Modal isOpen={isSupplyModalOpen} onClose={() => setIsSupplyModalOpen(false)} title={editingSupply ? 'Editar Producto / Armazón' : 'Nuevo Producto / Armazón'}>
                <form onSubmit={handleSupplySubmit} style={{ maxWidth: '640px' }}>
                    {/* Fotografía del Producto */}
                    <div style={{ marginBottom: '1.25rem', textAlign: 'center' }}>
                        <label style={{ display: 'block', fontWeight: '700', marginBottom: '0.5rem', color: '#0f172a', textAlign: 'left', fontSize: '0.9rem' }}>
                            Fotografía del Armazón / Producto
                        </label>
                        <div
                            onClick={() => document.getElementById('product-image-input').click()}
                            style={{
                                width: '100%', height: '180px', borderRadius: '12px', border: '2px dashed #cbd5e1',
                                backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', overflow: 'hidden', position: 'relative'
                            }}
                        >
                            {imagePreview ? (
                                <img src={imagePreview} alt="Previsualización" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '6px' }} />
                            ) : (
                                <>
                                    <i className="bi bi-camera" style={{ fontSize: '2.5rem', color: '#94a3b8' }}></i>
                                    <span style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '6px' }}>Haga clic para subir la foto del armazón</span>
                                </>
                            )}
                        </div>
                        <input
                            id="product-image-input"
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleImageChange}
                        />
                    </div>

                    {/* Tipo y Nombre */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>Categoría</label>
                            <select
                                value={supplyForm.supply_type}
                                onChange={e => setSupplyForm({ ...supplyForm, supply_type: e.target.value })}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            >
                                <option value="armazon">Armazón / Montura</option>
                                <option value="luna">Lunas / Cristales</option>
                                <option value="lente_contacto">Lentes de Contacto</option>
                                <option value="accesorio">Accesorios y Limpieza</option>
                                <option value="gotas">Gotas y Soluciones</option>
                                <option value="insumo">Insumo Clínico</option>
                            </select>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>Nombre / Descripción *</label>
                            <input
                                type="text"
                                required
                                placeholder="Ej: Montura Aviador Clásico Negro"
                                value={supplyForm.name}
                                onChange={e => setSupplyForm({ ...supplyForm, name: e.target.value })}
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            />
                        </div>
                    </div>

                    {/* Campos específicos para Armazones y Óptica */}
                    {supplyForm.supply_type === 'armazon' ? (
                        <div style={{ padding: '14px', backgroundColor: '#f1f5f9', borderRadius: '12px', marginBottom: '1.25rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '4px', color: '#0f172a' }}>Código SKU / Modelo *</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="Ej: w12236, mal2302"
                                        value={supplyForm.model_code}
                                        onChange={e => setSupplyForm({ ...supplyForm, model_code: e.target.value })}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700', fontFamily: 'monospace' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '4px', color: '#0f172a' }}>Línea</label>
                                    <input
                                        type="text"
                                        placeholder="Ej: CLASICA, MODERNA"
                                        value={supplyForm.linea}
                                        onChange={e => setSupplyForm({ ...supplyForm, linea: e.target.value })}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '4px', color: '#0f172a' }}>Marca</label>
                                    <input
                                        type="text"
                                        placeholder="Ej: Won, PrimeVision"
                                        value={supplyForm.brand}
                                        onChange={e => setSupplyForm({ ...supplyForm, brand: e.target.value })}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '4px', color: '#0f172a' }}>Categoría / Género</label>
                                    <select
                                        value={supplyForm.gender}
                                        onChange={e => setSupplyForm({ ...supplyForm, gender: e.target.value })}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                    >
                                        <option value="Hombre">Hombre</option>
                                        <option value="Niña">Niña</option>
                                        <option value="Niño">Niño</option>
                                        <option value="Mujer">Mujer</option>
                                        <option value="Unisex">Unisex</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '4px', color: '#0f172a' }}>Sucursal</label>
                                    <select
                                        value={supplyForm.sucursal}
                                        onChange={e => setSupplyForm({ ...supplyForm, sucursal: e.target.value })}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '600' }}
                                    >
                                        <option value="Sur">Sucursal Sur</option>
                                        <option value="Norte">Sucursal Norte</option>
                                        <option value="General">General / Ambas</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', marginBottom: '4px', color: '#0f172a' }}>Estado</label>
                                    <select
                                        value={supplyForm.status_armazon}
                                        onChange={e => {
                                            const st = e.target.value;
                                            setSupplyForm({
                                                ...supplyForm,
                                                status_armazon: st,
                                                current_stock: st === 'VENDIDO' ? 0 : (supplyForm.current_stock > 0 ? supplyForm.current_stock : 1)
                                            });
                                        }}
                                        style={{
                                            width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700',
                                            color: supplyForm.status_armazon === 'VENDIDO' ? '#dc2626' : '#16a34a'
                                        }}
                                    >
                                        <option value="DISPONIBLE">DISPONIBLE</option>
                                        <option value="VENDIDO">VENDIDO (Stock 0)</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '600', marginBottom: '2px', color: '#64748b' }}>Material (Opcional)</label>
                                    <input
                                        type="text"
                                        placeholder="Acetato / Metal / TR90"
                                        value={supplyForm.material}
                                        onChange={e => setSupplyForm({ ...supplyForm, material: e.target.value })}
                                        style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '600', marginBottom: '2px', color: '#64748b' }}>Color (Opcional)</label>
                                    <input
                                        type="text"
                                        placeholder="Negro / Carey / Transparente"
                                        value={supplyForm.color}
                                        onChange={e => setSupplyForm({ ...supplyForm, color: e.target.value })}
                                        style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ padding: '12px', backgroundColor: '#f1f5f9', borderRadius: '10px', marginBottom: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '2px' }}>Marca / Laboratorio</label>
                                    <input
                                        type="text"
                                        value={supplyForm.brand}
                                        onChange={e => setSupplyForm({ ...supplyForm, brand: e.target.value })}
                                        style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '2px' }}>Código SKU</label>
                                    <input
                                        type="text"
                                        value={supplyForm.model_code}
                                        onChange={e => setSupplyForm({ ...supplyForm, model_code: e.target.value })}
                                        style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Precios y Stock */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '700', fontSize: '0.82rem', marginBottom: '4px' }}>PVP Venta ($) *</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                required
                                value={supplyForm.sale_price}
                                onChange={e => setSupplyForm({ ...supplyForm, sale_price: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '800', color: '#0f172a' }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontWeight: '700', fontSize: '0.82rem', marginBottom: '4px', color: '#059669' }}>Precio Más ($)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={supplyForm.wholesale_price}
                                onChange={e => setSupplyForm({ ...supplyForm, wholesale_price: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700', color: '#059669' }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '4px', color: '#64748b' }}>Costo ($)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={supplyForm.cost_per_unit}
                                onChange={e => setSupplyForm({ ...supplyForm, cost_per_unit: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            />
                        </div>

                        {!editingSupply && (
                            <div>
                                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.82rem', marginBottom: '4px' }}>Stock Inicial</label>
                                <input
                                    type="number"
                                    min="0"
                                    required
                                    disabled={supplyForm.status_armazon === 'VENDIDO'}
                                    value={supplyForm.status_armazon === 'VENDIDO' ? 0 : supplyForm.current_stock}
                                    onChange={e => setSupplyForm({ ...supplyForm, current_stock: e.target.value })}
                                    style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700' }}
                                />
                            </div>
                        )}

                        <div>
                            <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '4px' }}>IVA Factura</label>
                            <select
                                value={supplyForm.iva ?? 15}
                                onChange={e => setSupplyForm({ ...supplyForm, iva: Number(e.target.value) })}
                                style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            >
                                {[0, 5, 12, 14, 15].map(t => <option key={t} value={t}>{t === 0 ? 'IVA 0%' : `IVA ${t}%`}</option>)}
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                        <button
                            type="button"
                            onClick={() => setIsSupplyModalOpen(false)}
                            style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: '600' }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            style={{ padding: '8px 22px', borderRadius: '8px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: '700' }}
                        >
                            {editingSupply ? 'Actualizar Producto' : 'Guardar Producto'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* MODAL REGISTRAR MOVIMIENTO DE STOCK */}
            <Modal isOpen={isMovementModalOpen} onClose={() => setIsMovementModalOpen(false)} title="Registrar Movimiento de Stock">
                <form onSubmit={handleMovementSubmit} style={{ maxWidth: '420px' }}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>Producto</label>
                        <input
                            type="text"
                            disabled
                            value={movementForm.supplyName}
                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#f1f5f9' }}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>Tipo Movimiento</label>
                            <select
                                value={movementForm.movement_type}
                                onChange={e => setMovementForm({ ...movementForm, movement_type: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            >
                                <option value="in">Ingreso (+)</option>
                                <option value="out">Egreso (-)</option>
                            </select>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>Cantidad</label>
                            <input
                                type="number"
                                min="1"
                                required
                                value={movementForm.quantity}
                                onChange={e => setMovementForm({ ...movementForm, quantity: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>Motivo / Referencia</label>
                        <input
                            type="text"
                            placeholder="Ej. Compra a proveedor, Ajuste físico"
                            value={movementForm.reason}
                            onChange={e => setMovementForm({ ...movementForm, reason: e.target.value })}
                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button
                            type="button"
                            onClick={() => setIsMovementModalOpen(false)}
                            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', fontWeight: '700' }}
                        >
                            Confirmar Movimiento
                        </button>
                    </div>
                </form>
            </Modal>

            {/* MODAL UNIDAD */}
            <Modal isOpen={isUnitModalOpen} onClose={() => setIsUnitModalOpen(false)} title={editingUnit ? 'Editar Unidad' : 'Nueva Unidad'}>
                <form onSubmit={handleUnitSubmit} style={{ maxWidth: '380px' }}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>Nombre</label>
                        <input
                            type="text"
                            required
                            placeholder="Ej. Unidad, Par, Caja"
                            value={unitForm.name}
                            onChange={e => setUnitForm({ ...unitForm, name: e.target.value })}
                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                        />
                    </div>
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ display: 'block', fontWeight: '600', fontSize: '0.85rem', marginBottom: '4px' }}>Abreviatura</label>
                        <input
                            type="text"
                            required
                            placeholder="Ej. un, par, cja"
                            value={unitForm.abbreviation}
                            onChange={e => setUnitForm({ ...unitForm, abbreviation: e.target.value })}
                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button type="button" onClick={() => setIsUnitModalOpen(false)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>Cancelar</button>
                        <button type="submit" style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer', fontWeight: '700' }}>Guardar</button>
                    </div>
                </form>
            </Modal>

            {/* MODAL IMPORTACIÓN MASIVA EXCEL */}
            <Modal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} title="📥 Importación Masiva de Armazones (Excel)">
                <form onSubmit={handleImportSubmit} style={{ maxWidth: '520px' }}>
                    <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', padding: '12px 14px', borderRadius: '10px', marginBottom: '1.25rem', color: '#1e40af', fontSize: '0.88rem', lineHeight: '1.45' }}>
                        <i className="bi bi-info-circle-fill me-2" style={{ color: '#2563eb' }}></i>
                        <strong>Importación Multi-Hoja Automática:</strong> El sistema lee automáticamente todas las hojas del archivo (ej: <strong>Hombre</strong>, <strong>Niña</strong>, <strong>Niño</strong>) en un solo archivo, sin necesidad de separarlos.
                    </div>

                    {/* Selector de Sucursal destino con tarjetas visuales */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ display: 'block', fontWeight: '700', fontSize: '0.9rem', marginBottom: '8px', color: '#0f172a' }}>
                            <i className="bi bi-shop me-2" style={{ color: '#2563eb' }}></i>Selecciona la Sucursal Destino *
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div
                                onClick={() => setImportSucursal('Sur')}
                                style={{
                                    padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: importSucursal === 'Sur' ? '#7c3aed' : '#e2e8f0',
                                    backgroundColor: importSucursal === 'Sur' ? '#f5f3ff' : '#fff',
                                    display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.15s'
                                }}
                            >
                                <input
                                    type="radio"
                                    name="import_sucursal_radio"
                                    checked={importSucursal === 'Sur'}
                                    onChange={() => setImportSucursal('Sur')}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <div>
                                    <div style={{ fontWeight: '800', color: importSucursal === 'Sur' ? '#6b21a8' : '#334155', fontSize: '0.95rem' }}>
                                        Sucursal Sur
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                        Inventario para Sucursal Sur
                                    </div>
                                </div>
                            </div>

                            <div
                                onClick={() => setImportSucursal('Norte')}
                                style={{
                                    padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: importSucursal === 'Norte' ? '#2563eb' : '#e2e8f0',
                                    backgroundColor: importSucursal === 'Norte' ? '#eff6ff' : '#fff',
                                    display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.15s'
                                }}
                            >
                                <input
                                    type="radio"
                                    name="import_sucursal_radio"
                                    checked={importSucursal === 'Norte'}
                                    onChange={() => setImportSucursal('Norte')}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <div>
                                    <div style={{ fontWeight: '800', color: importSucursal === 'Norte' ? '#1d4ed8' : '#334155', fontSize: '0.95rem' }}>
                                        Sucursal Norte
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                        Inventario para Sucursal Norte
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '8px', padding: '6px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.8rem', color: '#475569' }}>
                            <i className="bi bi-arrow-right-short" style={{ color: '#2563eb' }}></i>
                            Los armazones de este archivo se guardarán asignados a: <strong>Sucursal {importSucursal}</strong>
                        </div>
                    </div>

                    {/* Selector de archivo Excel */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ display: 'block', fontWeight: '700', fontSize: '0.85rem', marginBottom: '6px', color: '#0f172a' }}>
                            Archivo Excel (.xlsx, .xls) *
                        </label>
                        <div
                            onClick={() => document.getElementById('excel-file-input').click()}
                            style={{
                                border: '2px dashed #059669', backgroundColor: '#f0fdf4', borderRadius: '12px',
                                padding: '1.5rem', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s'
                            }}
                        >
                            <i className="bi bi-file-earmark-spreadsheet" style={{ fontSize: '2.5rem', color: '#059669' }}></i>
                            <div style={{ fontWeight: '700', color: '#065f46', marginTop: '6px' }}>
                                {importFile ? importFile.name : 'Haz clic para seleccionar tu archivo Excel'}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#047857', marginTop: '2px' }}>
                                {importFile ? `${(importFile.size / 1024).toFixed(1)} KB` : 'Archivos con hojas: Hombre, Niña, Niño'}
                            </div>
                        </div>
                        <input
                            id="excel-file-input"
                            type="file"
                            accept=".xlsx, .xls"
                            style={{ display: 'none' }}
                            onChange={e => setImportFile(e.target.files[0] || null)}
                        />
                    </div>

                    {/* Casilla armazones vendidos */}
                    <div style={{ backgroundColor: '#f8fafc', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '1.25rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '0.88rem', color: '#0f172a' }}>
                            <input
                                type="checkbox"
                                checked={importVendidos}
                                onChange={e => setImportVendidos(e.target.checked)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            Importar también armazones VENDIDOS con Stock 0
                        </label>
                        <p style={{ margin: '4px 0 0 28px', fontSize: '0.8rem', color: '#64748b', lineHeight: '1.4' }}>
                            Mantiene el histórico y catálogo de modelos vendidos listos para reponer cuando vuelvan a ingresar sin tener que reescribirlos.
                        </p>
                    </div>

                    {/* Resultado tras importar */}
                    {importResult && (
                        <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', padding: '12px 14px', borderRadius: '10px', marginBottom: '1.25rem', color: '#065f46' }}>
                            <div style={{ fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', fontSize: '0.95rem' }}>
                                <i className="bi bi-check-circle-fill"></i> {importResult.message}
                            </div>
                            <div style={{ fontSize: '0.85rem' }}>
                                <strong>Sucursal:</strong> {importResult.sucursal} | <strong>Total:</strong> {importResult.total_imported} | <strong>Nuevos:</strong> {importResult.created} | <strong>Actualizados:</strong> {importResult.updated}
                            </div>
                            {importResult.sheet_stats && (
                                <div style={{ marginTop: '8px', fontSize: '0.8rem', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {Object.entries(importResult.sheet_stats).map(([sh, cnt]) => (
                                        <span key={sh} style={{ backgroundColor: '#d1fae5', color: '#065f46', padding: '3px 8px', borderRadius: '12px', fontWeight: '700' }}>
                                            {sh}: {cnt} armazones
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                        <button
                            type="button"
                            onClick={() => setIsImportModalOpen(false)}
                            style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: '600' }}
                        >
                            {importResult ? 'Cerrar' : 'Cancelar'}
                        </button>
                        <button
                            type="submit"
                            disabled={importLoading || !importFile}
                            style={{
                                padding: '8px 24px', borderRadius: '8px', border: 'none',
                                background: importLoading || !importFile ? '#94a3b8' : '#059669',
                                color: '#fff', cursor: importLoading || !importFile ? 'not-allowed' : 'pointer', fontWeight: '700',
                                display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.2)'
                            }}
                        >
                            {importLoading ? (
                                <>
                                    <i className="bi bi-arrow-repeat spin"></i> Procesando Excel...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-cloud-arrow-up-fill"></i> Iniciar Importación
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Barra flotante de acciones masivas */}
            {selectedSupplies.length > 0 && (
                <div style={{
                    position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
                    backgroundColor: '#0f172a', color: '#fff', padding: '12px 24px', borderRadius: '14px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', gap: '16px',
                    zIndex: 1000, border: '1px solid #334155', flexWrap: 'wrap'
                }}>
                    <div style={{ fontWeight: '700', fontSize: '0.92rem' }}>
                        <i className="bi bi-check2-square me-2" style={{ color: '#38bdf8' }}></i>
                        {selectedSupplies.length} armazones seleccionados
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            type="button"
                            onClick={() => handleBulkMoveSucursal('Sur')}
                            disabled={isBulkMoving}
                            style={{
                                backgroundColor: '#7c3aed', color: '#fff', border: 'none', padding: '8px 16px',
                                borderRadius: '8px', fontWeight: '700', cursor: isBulkMoving ? 'wait' : 'pointer', fontSize: '0.85rem',
                                display: 'flex', alignItems: 'center', gap: '6px'
                            }}
                        >
                            <i className="bi bi-arrow-right-circle"></i> Mover a Sucursal Sur
                        </button>
                        <button
                            type="button"
                            onClick={() => handleBulkMoveSucursal('Norte')}
                            disabled={isBulkMoving}
                            style={{
                                backgroundColor: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px',
                                borderRadius: '8px', fontWeight: '700', cursor: isBulkMoving ? 'wait' : 'pointer', fontSize: '0.85rem',
                                display: 'flex', alignItems: 'center', gap: '6px'
                            }}
                        >
                            <i className="bi bi-arrow-right-circle"></i> Mover a Sucursal Norte
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedSupplies([])}
                            style={{
                                backgroundColor: '#334155', color: '#cbd5e1', border: 'none', padding: '8px 14px',
                                borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
            {/* MODAL ELIMINAR / VACIAR INVENTARIO */}
            <Modal isOpen={isClearModalOpen} onClose={() => !clearLoading && setIsClearModalOpen(false)} title="🗑️ Eliminar Inventario de Armazones">
                <form onSubmit={handleClearInventory} style={{ maxWidth: '520px' }}>
                    <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', padding: '14px 16px', borderRadius: '12px', marginBottom: '1.25rem', color: '#991b1b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                        <div style={{ fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '0.98rem' }}>
                            <i className="bi bi-exclamation-triangle-fill" style={{ color: '#dc2626', fontSize: '1.2rem' }}></i>
                            ¿Deseas reiniciar y vaciar el inventario?
                        </div>
                        Esta opción eliminará los armazones registrados para que puedas realizar una nueva importación limpia desde tu archivo Excel sin duplicados ni cruce de sucursales.
                    </div>

                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ display: 'block', fontWeight: '700', fontSize: '0.9rem', marginBottom: '8px', color: '#0f172a' }}>
                            ¿Qué deseas eliminar? *
                        </label>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div
                                onClick={() => setClearScope('all')}
                                style={{
                                    padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: clearScope === 'all' ? '#dc2626' : '#e2e8f0',
                                    backgroundColor: clearScope === 'all' ? '#fff5f5' : '#fff',
                                    display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.15s'
                                }}
                            >
                                <input
                                    type="radio"
                                    name="clear_scope"
                                    checked={clearScope === 'all'}
                                    onChange={() => setClearScope('all')}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <div>
                                    <div style={{ fontWeight: '800', color: clearScope === 'all' ? '#dc2626' : '#1e293b', fontSize: '0.95rem' }}>
                                        🌐 Eliminar TODO el inventario de armazones (Sur y Norte)
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                        Vacía completamente el catálogo de armazones de ambas sucursales.
                                    </div>
                                </div>
                            </div>

                            <div
                                onClick={() => setClearScope('Sur')}
                                style={{
                                    padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: clearScope === 'Sur' ? '#7c3aed' : '#e2e8f0',
                                    backgroundColor: clearScope === 'Sur' ? '#f5f3ff' : '#fff',
                                    display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.15s'
                                }}
                            >
                                <input
                                    type="radio"
                                    name="clear_scope"
                                    checked={clearScope === 'Sur'}
                                    onChange={() => setClearScope('Sur')}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <div>
                                    <div style={{ fontWeight: '800', color: clearScope === 'Sur' ? '#6b21a8' : '#1e293b', fontSize: '0.95rem' }}>
                                        🏢 Eliminar únicamente armazones de Sucursal Sur
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                        Conserva los armazones del Norte y solo vacía el Sur.
                                    </div>
                                </div>
                            </div>

                            <div
                                onClick={() => setClearScope('Norte')}
                                style={{
                                    padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: clearScope === 'Norte' ? '#2563eb' : '#e2e8f0',
                                    backgroundColor: clearScope === 'Norte' ? '#eff6ff' : '#fff',
                                    display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.15s'
                                }}
                            >
                                <input
                                    type="radio"
                                    name="clear_scope"
                                    checked={clearScope === 'Norte'}
                                    onChange={() => setClearScope('Norte')}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <div>
                                    <div style={{ fontWeight: '800', color: clearScope === 'Norte' ? '#1d4ed8' : '#1e293b', fontSize: '0.95rem' }}>
                                        🏢 Eliminar únicamente armazones de Sucursal Norte
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                        Conserva los armazones del Sur y solo vacía el Norte.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                        <button
                            type="button"
                            onClick={() => setIsClearModalOpen(false)}
                            disabled={clearLoading}
                            style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: '600' }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={clearLoading}
                            style={{
                                padding: '8px 22px', borderRadius: '8px', border: 'none',
                                background: clearLoading ? '#f87171' : '#dc2626',
                                color: '#fff', cursor: clearLoading ? 'wait' : 'pointer', fontWeight: '800',
                                display: 'flex', alignItems: 'center', gap: '8px',
                                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)'
                            }}
                        >
                            {clearLoading ? (
                                <>
                                    <i className="bi bi-arrow-repeat spin"></i> Eliminando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-trash3-fill"></i> Confirmar y Eliminar
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default Bodega;
